import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Image } from '@mantine/core';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import LanguageSelector from '@/components/Common/LanguageSelector';
import { requestPasswordReset } from '@mygpt/app';

export default function ForgotPassword() {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const companyLogoUrl = '/logo.svg';
  const companyName = 'MyGPT';

  const forgotPasswordTitle = t('forgot_password.title');
  const forgotPasswordDescription = t('forgot_password.description');
  const emailLabelText = t('forgot_password.email_label');
  const emailPlaceholderText = t('forgot_password.email_placeholder');
  const emailRequiredText = t('forgot_password.email_required');
  const sendResetLinkText = t('forgot_password.send_reset_link');
  const rememberPasswordText = t('forgot_password.remember_password');
  const signInLinkText = t('forgot_password.sign_in');

  useEffect(() => {
    if (timer <= 0) return;
    const timeoutId = window.setTimeout(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [timer]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (error) setError('');
    if (message) setMessage('');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      setError(emailRequiredText);
      setMessage('');
      return;
    }

    try {
      setIsLoading(true);
      await requestPasswordReset({ email: email.trim() });
      setMessage(t('forgot_password.reset_link_sent'));
      setError('');
      setTimer(60);
    } catch (err) {
      const errorMessage =
        typeof err === 'object' &&
        err !== null &&
        'errorMsg' in err &&
        typeof (err as { errorMsg?: string }).errorMsg === 'string'
          ? (err as { errorMsg: string }).errorMsg
          : t('forgot_password.reset_link_failed');
      setError(errorMessage);
      setMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col justify-center bg-gray-50 py-4 px-4 md:py-12 md:px-6 lg:px-8">
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">
        <LanguageSelector variant="button" />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Image
          src={companyLogoUrl}
          fallbackSrc="/logo.svg"
          alt={companyName}
          className="mx-auto h-10 w-auto md:h-12"
          fit="contain"
        />

        <h2 className="mt-4 text-center text-2xl md:mt-6 md:text-3xl font-bold tracking-tight text-gray-900">
          {forgotPasswordTitle}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">{forgotPasswordDescription}</p>
      </div>

      <div className="mt-6 md:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <form
          className="space-y-6 bg-white py-6 px-4 md:py-8 md:px-6 shadow rounded-xl border border-gray-200 sm:px-10"
          onSubmit={handleSubmit}
        >
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          {message && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-700">{message}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
              {emailLabelText}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={handleChange}
              className="block w-full rounded-xl border border-gray-300 px-4 py-3 md:py-3 text-gray-900 placeholder-gray-400 bg-gray-50 hover:border-gray-400 focus:border-primary-500 focus:bg-white focus:ring-1 focus:ring-primary-500 transition-all duration-200 text-base md:text-sm"
              placeholder={emailPlaceholderText}
            />
          </div>

          <Button
            type="submit"
            fullWidth
            size="md"
            radius="md"
            loading={isLoading}
            loaderProps={{ size: 'sm', color: 'white' }}
            className="bg-gray-900 hover:bg-gray-800 text-white font-medium text-base md:text-sm py-3.5 md:py-3 shadow-sm transition-colors duration-200"
            disabled={timer > 0 || isLoading}
          >
            {timer > 0 ? t('forgot_password.resend_in', { timer }) : sendResetLinkText}
          </Button>
        </form>
      </div>

      <p className="mt-4 text-center text-sm text-gray-500">
        {rememberPasswordText}{' '}
        <Link to="/auth/login" className="font-semibold text-primary-700 hover:text-primary-600">
          {signInLinkText}
        </Link>
      </p>
    </div>
  );
}
